/**
 * Viewport Context Menu
 *
 * Adds a right-click context menu directly in the 3D viewport canvas.
 * When the user right-clicks on an Image or Video object, a floating menu
 * appears with inference commands generated from the plugin registry; a node
 * that references a library asset also offers "Copy to scene", and any
 * selection can be promoted into the library with "Save as asset"
 * (CONTENT_MODEL_SPEC §2.6, §3.5).
 */

import type { TraceViewerElement } from 'trace-viewer';
import type { SceneObject } from 'trace-viewer/scene';
import { getContextMenuEntries } from './plugins/registry';
import { runFromContextMenu } from './inference-context-menu-service';
import { copyReferenceToScene } from './cms/copy-reference-action';
import { getActiveCmsManager } from './cms/active-manager';
import { canSaveAsAsset, saveSelectionAsAsset } from './cms/save-as-asset-action';
import { PromotionRefusedError } from './cms/save-as-asset';
import { selectionForAction } from './active-selection';
import { showSpinnerToast } from './toast';

const getViewer = (): TraceViewerElement | null =>
    document.querySelector('trace-viewer');

// ============================================================================
// Floating context menu element
// ============================================================================

let menuEl: HTMLElement | null = null;
let dismissListener: ((e: MouseEvent) => void) | null = null;

function createMenu(): HTMLElement {
    const el = document.createElement('div');
    el.className = 'viewport-context-menu';
    document.body.appendChild(el);
    return el;
}

function showMenu(x: number, y: number, items: { label: string; action: () => void }[]): void {
    hideMenu();

    menuEl = createMenu();
    for (const item of items) {
        const btn = document.createElement('button');
        btn.textContent = item.label;
        btn.addEventListener('click', () => {
            hideMenu();
            item.action();
        });
        menuEl.appendChild(btn);
    }

    menuEl.style.left = `${x}px`;
    menuEl.style.top = `${y}px`;

    // Ensure it stays within the viewport
    requestAnimationFrame(() => {
        if (!menuEl) return;
        const rect = menuEl.getBoundingClientRect();
        if (rect.right > window.innerWidth) {
            menuEl.style.left = `${window.innerWidth - rect.width - 4}px`;
        }
        if (rect.bottom > window.innerHeight) {
            menuEl.style.top = `${window.innerHeight - rect.height - 4}px`;
        }
    });

    // Dismiss on next click outside the menu
    dismissListener = (e: MouseEvent) => {
        if (menuEl && !menuEl.contains(e.target as Node)) {
            hideMenu();
        }
    };
    // Use setTimeout so the current event cycle doesn't immediately dismiss
    setTimeout(() => {
        window.addEventListener('pointerdown', dismissListener!);
    }, 0);
}

function hideMenu(): void {
    if (dismissListener) {
        window.removeEventListener('pointerdown', dismissListener);
        dismissListener = null;
    }
    if (menuEl) {
        menuEl.remove();
        menuEl = null;
    }
}

// ============================================================================
// Pick SceneObject from mouse position
// ============================================================================

function pickSceneObject(clientX: number, clientY: number): SceneObject | null {
    const viewer = getViewer();
    if (!viewer?.traceScene || !viewer.scene) return null;

    const scene = viewer.scene;
    const canvas = scene.getEngine().getRenderingCanvas();
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;

    const pickResult = scene.pick(x, y);
    if (!pickResult?.hit || !pickResult.pickedMesh) return null;

    // Walk up the transform hierarchy to find the owning SceneObject
    const traceScene = viewer.traceScene;
    let current = pickResult.pickedMesh;
    while (current) {
        const so = traceScene.findSceneObjectByTransform(current);
        if (so) return so;
        current = current.parent as typeof current;
    }

    return null;
}

// ============================================================================
// Context menu handler
// ============================================================================

function onContextMenu(e: MouseEvent): void {
    const so = pickSceneObject(e.clientX, e.clientY);
    if (!so) return;

    const entries = getContextMenuEntries().filter(entry => entry.predicate(so));
    const items = entries.map(entry => ({
        label: entry.label,
        action: () => {
            runFromContextMenu(entry, so).catch((err: unknown) =>
                console.error(`[Viewport Context Menu] ${entry.label} failed:`, err)
            );
        },
    }));

    // Freezing a live library reference (CONTENT_MODEL_SPEC §2.6). Offered
    // only where it means something: the node has to BE a reference
    // (`contentRef` — the asset-file-URL shape) and there has to be a scene
    // folder to copy the bytes into.
    const cms = getActiveCmsManager();
    if (cms && so.contentRef) {
        items.push({
            label: 'Copy to scene',
            action: () => {
                void copyReferenceToScene(so, cms);
            },
        });
    }

    // Promotion, the inverse gesture (CONTENT_MODEL_SPEC §3.5). Offered only
    // where it can land: a promotion creates an asset in the folder open in
    // the Studio panel, so with no folder (or no sign-in) there is nowhere to
    // put it. A right-click inside the selection promotes the whole selection
    // — the viewer groups it under one root in the exported document.
    if (canSaveAsAsset()) {
        const selection = selectionForAction(so);
        const suffix = selection.length > 1 ? ` (${selection.length} objects)` : '';
        items.push({
            label: `Save as asset${suffix}…`,
            action: () => { void runSaveAsAsset(selection, true); },
        });
        items.push({
            label: `Save as asset, keep inline${suffix}…`,
            action: () => { void runSaveAsAsset(selection, false); },
        });
    }

    if (items.length === 0) return;

    e.preventDefault();
    showMenu(e.clientX, e.clientY, items);
}

/**
 * Run a promotion from the menu: ask for the name, then show what happened.
 *
 * The **offenders** case is the one worth the extra branch. A promotion the
 * closure rule refuses is one the server would refuse too (spec §2.5), and the
 * author needs to know *which* references are the problem — so the full list,
 * index and class and reason, goes to the console, and the toast says how many
 * and where to look. A single collapsed error message is what this is not.
 */
async function runSaveAsAsset(selection: readonly SceneObject[], replaceWithReference: boolean): Promise<void> {
    const viewer = getViewer();
    if (!viewer) return;

    const suggested = selection.length === 1 ? selection[0].name : 'Component';
    const name = window.prompt('Save as asset — name:', suggested);
    if (name === null) return;

    // An emptied field means the suggestion, not "whatever the action falls
    // back to" — so the name in the toast is the name on the asset.
    const title = name.trim() || suggested;
    const dismiss = showSpinnerToast(`Saving "${title}" to the library…`);
    try {
        const result = await saveSelectionAsAsset(viewer, selection, { name: title, replaceWithReference });
        dismiss(
            `Saved "${result.name}" to the library`
            + `${result.reference ? ' and replaced the selection with a reference' : ''}`,
            'success',
        );
    } catch (error) {
        if (error instanceof PromotionRefusedError) {
            console.error(`[SaveAsAsset] ${error.message}`);
            dismiss(
                `Cannot save as asset: ${error.offenders.length} reference(s) an asset may not carry — see console`,
                'error',
            );
            return;
        }
        console.error('[SaveAsAsset] Failed:', error);
        dismiss(`Save as asset failed: ${error instanceof Error ? error.message : String(error)}`, 'error');
    }
}

// ============================================================================
// Setup / teardown
// ============================================================================

let attachedCanvas: HTMLCanvasElement | null = null;

/**
 * Attach the viewport context menu to the current canvas.
 * Safe to call repeatedly — re-attaches if the canvas changed.
 */
export function setupViewportContextMenu(): void {
    const viewer = getViewer();
    const canvas = viewer?.scene?.getEngine().getRenderingCanvas() ?? null;

    if (canvas === attachedCanvas) return;

    teardownViewportContextMenu();

    if (canvas) {
        canvas.addEventListener('contextmenu', onContextMenu);
        attachedCanvas = canvas;
    }
}

/** Remove the viewport context menu listener. */
export function teardownViewportContextMenu(): void {
    hideMenu();
    if (attachedCanvas) {
        attachedCanvas.removeEventListener('contextmenu', onContextMenu);
        attachedCanvas = null;
    }
}
